import React from 'react'
import { observer } from 'mobx-react-lite'

import Alert from '@mui/material/Alert'
import Snackbar from '@mui/material/Snackbar'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'

import { Drawer, useHelpStore } from '../../../common'
import { type InstitutionData, type HelpCopy } from '../../../core'
import { RequestSupportHeader } from './RequestSupportHeader'

interface MissingInstitutionProps {
  copy: HelpCopy
  isOpen: boolean
  onClose: () => void
  onSave: (formData: InstitutionData) => void
}

const initialErrors = {
  institutionName: '',
  institutionWebsite: '',
}

const initialFormData = {
  institutionName: '',
  institutionLoginPage: '',
  institutionWebsite: '',
}

const MissingInstitution: React.FC<MissingInstitutionProps> = observer(
  ({ copy, isOpen, onClose, onSave }) => {
    const { alert, isLoading, setAlert } = useHelpStore()
    const [errors, setErrors] = React.useState<{ [key: string]: string }>(initialErrors)
    const [formData, setFormData] = React.useState<InstitutionData>(initialFormData)

    const fields = [
      { name: 'institutionName', required: true },
      { name: 'institutionWebsite', required: true },
      { name: 'institutionLoginPage', required: false },
    ].map((field, index) => ({
      ...field,
      label: copy.popular_topics_missing_institution_fields[index.toString()],
    }))

    const handleClose = () => {
      setErrors(initialErrors)
      setFormData(initialFormData)
      onClose()
    }

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const {
        target: { name, value },
      } = event
      setFormData((prevData) => ({
        ...prevData,
        [name]: value,
      }))
    }

    const handleSave = () => {
      const newErrors: Record<string, string> = {}
      let isValid = true

      fields.forEach(({ label, name, required }) => {
        const value = formData[name as keyof InstitutionData]

        if (required && !value) {
          newErrors[name] = `${label} ${copy.request_support_required_message}`
          isValid = false
        }

        const urlPattern = /(.+)\.(.{2,})/g
        if (
          (name === 'institutionWebsite' || name === 'institutionLoginPage') &&
          value &&
          !urlPattern.test(value)
        ) {
          newErrors[name] = copy.request_support_valid_url_message
          isValid = false
        }
      })

      setErrors(newErrors)

      if (isValid) {
        onSave(formData)
        setFormData(initialFormData)
      }
    }

    return (
      <Drawer
        isOpen={isOpen}
        onClose={handleClose}
        onPrimaryAction={handleSave}
        secondaryColor="primary"
        title={copy.request_support}
      >
        <RequestSupportHeader
          content={copy.popular_topics_missing_institution_content}
          hasIcon={false}
          reverse={true}
          subtitle={copy.request_support_institution_short_content}
          title={copy.popular_topics_missing_institution}
        />
        <Stack sx={{ gap: 24, p: 24 }}>
          {fields.map(({ label, name }) => (
            <TextField
              error={!!errors[name]}
              fullWidth={true}
              helperText={errors[name] || ''}
              key={name}
              label={label}
              name={name}
              onChange={handleInputChange}
              slotProps={{ inputLabel: { shrink: true } }}
              value={formData[name as keyof InstitutionData]}
            />
          ))}
        </Stack>
        {alert.message && (
          <Snackbar
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            autoHideDuration={3500}
            onClose={() => setAlert('')}
            open={!!alert.message && alert.severity === 'error' && !isLoading}
          >
            <Alert onClose={() => setAlert('')} severity={alert.severity} variant="filled">
              {alert.message}
            </Alert>
          </Snackbar>
        )}
      </Drawer>
    )
  },
)

export default MissingInstitution
